import { formatMoney, type Voucher } from "./api";

export function remainingVoucherCents(voucher: Voucher): number {
  return voucher.status === "unpaid" ? Math.max(0, voucher.amountCents - (voucher.partPaidCents ?? 0)) : 0;
}

/**
 * A voucher paid in more than one go, part by part: "Deposit 132.00 (10%) on 30 Jun",
 * "Balance 1,188.00 (90%) on 1 Jul" (D, 10 October 2026: "it is not a single payment").
 */
export function PaymentParts({
  totalCents,
  payments,
  done,
}: {
  readonly totalCents: number;
  readonly payments: readonly { readonly day: string; readonly amountCents: number; readonly thisLine?: boolean }[];
  /** Whether the parts add up to the whole: the last is then the balance. */
  readonly done: boolean;
}): React.ReactElement | null {
  if (payments.length < 2 && done) return null;
  if (payments.length === 0) return null;
  return (
    <div className="text-[0.75rem] mt-1">
      {payments.map((payment, index) => {
        const share = totalCents > 0 ? Math.round((payment.amountCents / totalCents) * 100) : 0;
        const name = index === 0 ? "Deposit" : done && index === payments.length - 1 ? "Balance" : `Payment ${index + 1}`;
        const day = new Date(`${payment.day}T12:00:00+08:00`).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
        return (
          <div key={`${payment.day}-${index}`} className={payment.thisLine ? "font-semibold" : ""}>
            {name} S$ {formatMoney(payment.amountCents)} ({share}%) on {day}
            {payment.thisLine ? " - this payment" : ""}
          </div>
        );
      })}
    </div>
  );
}

/** D, 7 October: a deposit must leave the balance plainly visible on both linked rows. */
export function VoucherBalance({ voucher }: { readonly voucher: Voucher }): React.ReactElement | null {
  if (voucher.status === "paid" && (voucher.payments?.length ?? 0) > 1) {
    return <div className="mt-1 text-[#8c9097] dark:text-white/50"><PaymentParts totalCents={voucher.amountCents} payments={voucher.payments ?? []} done /></div>;
  }
  if (voucher.status !== "unpaid") return null;
  return <div className="mt-2 p-2 rounded border border-warning/40 bg-warning/10 min-w-[10rem]" aria-label={`Payment outstanding for ${voucher.number}`}>
    <div className="text-warning font-semibold text-[0.75rem]"><i className="ri-error-warning-line me-1" aria-hidden="true" />Payment outstanding</div>
    <div className="font-bold text-[0.9375rem]">S$ {formatMoney(remainingVoucherCents(voucher))} remaining</div>
    <div className="text-[0.75rem]">Paid S$ {formatMoney(voucher.partPaidCents ?? 0)} of S$ {formatMoney(voucher.amountCents)}</div>
    <PaymentParts totalCents={voucher.amountCents} payments={voucher.payments ?? []} done={false} />
  </div>;
}
